import type { VenueProfileFieldError } from '@/lib/api/errors'

import type {
  BusinessType,
  OperationVerificationPrefill,
  OperationVerificationSubmitInput,
} from './operationVerificationTypes'

/**
 * 운영 인증 신청 폼 상태 ↔ 요청/응답 매핑 + 전량 수집 클라이언트 검증(`#771` §2·§8, design.md §2).
 * `venueRegisterViewModel.ts`·`venueProfileViewModel.ts` 와 같은 컨벤션을 따른다.
 *
 * 🚨 `if (status === ...)` 를 페이지에 흩지 않는다 — 이 파일이 규칙의 단일 출처다.
 */

export interface OperationVerificationDocumentState {
  status: 'idle' | 'uploading' | 'done' | 'error'
  /** `done` 이면 업로드한 파일명, `idle` 이면 재신청 prefill 의 직전 파일명(표시 전용, key 없음). */
  fileName: string | null
  /** `done` 일 때만 값이 있다 — 제출 시 이 값만 서버로 보낸다. */
  documentKey: string | null
  errorMessage: string | null
  /**
   * 이미지 파일(jpg/jpeg/png)을 선택했을 때만 `URL.createObjectURL(file)` 값 — 썸네일 미리보기 전용
   * 로컬 상태다(`#887`). PDF·미선택·재신청 prefill(파일 자체가 없다) 상태는 `null`. 서버로 보내지 않는다.
   */
  previewUrl: string | null
  /**
   * 선택한 파일의 바이트 크기(선택적) — 이 폼(운영 인증 신청)의 제출 바디는 크기를 받지 않아 여기선
   * 쓰이지 않는다. 사업자 정보 수정 요청 폼(`venueBusinessInfoViewModel.ts`, `#1191`)이 같은 상태
   * 머신·컴포넌트(`OperationVerificationDocumentField`)를 재사용하면서 `documentSize`(api-spec §D7-M1
   * 필수 필드)를 채우기 위해 추가했다. `done` 이 아니면 `null`.
   */
  fileSizeBytes?: number | null
}

/** 텍스트 입력은 항상 문자열로 다룬다(빈 문자열이 유효한 중간 상태) — 저장 직전에만 `null` 로 정규화한다. */
export interface OperationVerificationFormState {
  businessType: BusinessType
  representativeName: string
  businessNumber: string
  contact: string
  operationAddress: string
  /** `<input type="date">` 값 — `yyyy-MM-dd`. */
  operationStartedOn: string
  additionalNote: string
  document: OperationVerificationDocumentState
}

function emptyDocumentState(): OperationVerificationDocumentState {
  return { status: 'idle', fileName: null, documentKey: null, errorMessage: null, previewUrl: null }
}

export function emptyOperationVerificationFormState(): OperationVerificationFormState {
  return {
    businessType: 'INDIVIDUAL',
    representativeName: '',
    businessNumber: '',
    contact: '',
    operationAddress: '',
    operationStartedOn: '',
    additionalNote: '',
    document: emptyDocumentState(),
  }
}

/**
 * 재신청 prefill(EP-3) → 폼 초기값(design.md §2 아키텍처 개요 1, `#867` 설계 §2 자율 결정 5).
 * 🚨 서류는 재사용 불가라 `documentFileName` 은 표시만 하고 `documentKey` 는 채우지 않는다
 * (api-spec §4 "key 는 주지 않으며 재신청 시 파일을 다시 첨부해야 한다") — 재첨부 전까지는 `idle` 상태다.
 *
 * 🚨 `representativeName` 은 `hasPrevious` 와 **무관하게** 항상 prefill 값을 쓴다 — 백엔드(`#865`
 * D-D1)가 이 값을 재신청 여부와 상관없이 항상 회원 실명으로 확정해 내려주고, 제출 시에도 요청 본문의
 * 값을 무시하고 서버가 실명으로 덮어쓴다. 화면은 그 사실을 그대로 반영해 이 필드를 읽기전용으로 그린다
 * (`VenueOperationVerificationPage.tsx`).
 */
export function prefillToFormState(prefill: OperationVerificationPrefill): OperationVerificationFormState {
  if (!prefill.hasPrevious) {
    return { ...emptyOperationVerificationFormState(), representativeName: prefill.representativeName ?? '' }
  }

  return {
    businessType: prefill.businessType ?? 'INDIVIDUAL',
    representativeName: prefill.representativeName ?? '',
    businessNumber: prefill.businessNumber ?? '',
    contact: prefill.contact ?? '',
    operationAddress: prefill.operationAddress ?? '',
    operationStartedOn: prefill.operationStartedOn ?? '',
    additionalNote: prefill.additionalNote ?? '',
    document: { ...emptyDocumentState(), fileName: prefill.documentFileName },
  }
}

/**
 * 재제출 차단 판정(design.md §2 자율 결정 3) — 심사 중인 회차가 있으면 폼 대신 진행중 안내를 보여준다.
 * 반려(`REJECTED`)·승인(`APPROVED`, 기한초과 갱신) 은 전부 `false` — 폼을 그대로 연다(자율 결정 9).
 */
export function isReviewPending(prefill: OperationVerificationPrefill): boolean {
  return prefill.hasPrevious && prefill.progress === 'PENDING'
}

/** en-CA 로케일은 `yyyy-MM-dd` 를 그대로 낸다(`<input type="date">`·서버 `LocalDate` 와 같은 포맷). */
const KST_DATE_FMT = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Seoul' })

/** KST(Asia/Seoul) 기준 "오늘" — 날짜 입력의 `max` 값·미래일자 검증에 함께 쓴다. */
export function todayKstDate(now: Date = new Date()): string {
  return KST_DATE_FMT.format(now)
}

export const OPERATION_VERIFICATION_LIMITS = {
  representativeName: 50,
  operationAddress: 200,
  additionalNote: 500,
} as const

export const OPERATION_VERIFICATION_DOCUMENT_LIMITS = {
  maxSizeBytes: 10 * 1024 * 1024,
  allowedExtensions: ['pdf', 'jpg', 'jpeg', 'png'] as string[],
  allowedExtensionsLabel: 'PDF, JPG, PNG',
} as const

const IMAGE_DOCUMENT_EXTENSIONS = ['jpg', 'jpeg', 'png']

/**
 * 파일명 확장자가 이미지(jpg/jpeg/png)인지 판정 — 썸네일 미리보기를 만들지 결정하는 조건이다(`#887`).
 * PDF 는 브라우저 표준 API만으로 이미지 렌더링이 불가능해 미리보기를 만들지 않는다.
 */
export function isOperationVerificationImageFile(fileName: string): boolean {
  const extension = fileName.split('.').pop()?.toLowerCase() ?? ''
  return IMAGE_DOCUMENT_EXTENSIONS.includes(extension)
}

/**
 * 하이픈·공백을 제거한 숫자만 남긴다 — 서버가 저장 전 하는 정규화와 같은 규칙(api-spec §2).
 * 🚨 `export` — 사업자 정보 수정 요청 폼(`venueBusinessInfoViewModel.ts`, `#1191`)이 같은 사업자번호
 * 정규화 규칙을 그대로 재사용한다(design.md §2 자율 결정 — 복붙 시 서버 정책 변경 때 한쪽만 고쳐질 위험).
 */
export function stripNonDigits(value: string): string {
  return value.replace(/\D/g, '')
}

/** 🚨 `export` — 위와 같은 이유로 `venueBusinessInfoViewModel.ts`가 재사용한다. */
export const BUSINESS_NUMBER_PATTERN = /^\d{10}$/
const CONTACT_PATTERN = /^\d{9,11}$/

const FIELD_LABELS: Record<string, string> = {
  representativeName: '대표자 성명',
  businessNumber: '사업자등록번호',
  contact: '연락처',
  operationAddress: '구장 운영 주소',
  additionalNote: '추가 안내 사항',
}

/**
 * 제출 버튼 활성 조건(`#887`) — 서버 필수 4종(`businessType`·`representativeName`·`businessNumber`·
 * `contact`) 중 클라이언트가 값을 쥔 3개가 비어 있지 않은지만 본다. `businessType`은 `Select`가 항상
 * 유효한 기본값을 가져 검증 대상이 아니다(`validateOperationVerificationForm` 관례와 동일).
 *
 * 🚨 형식 유효성(자릿수 등)은 여기서 보지 않는다 — 그건 `validateOperationVerificationForm`이 제출
 * 클릭 후 필드 에러로 안내한다(design.md §2 자율 결정 2, 기존 "클릭 시 한 번에 모아 보여주는" UX 유지).
 */
export function isOperationVerificationFormComplete(form: OperationVerificationFormState): boolean {
  return (
    form.representativeName.trim().length > 0 &&
    form.businessNumber.trim().length > 0 &&
    form.contact.trim().length > 0
  )
}

/**
 * 클라이언트 사전 검증(api-spec §8). 서버가 최종 판정자이고, 이 표는 api-spec §2 검증표를 미러링한다.
 * 🚨 모든 위반을 한 번에 수집한다(fail-fast 아님 — 서버와 같은 UX, `venueRegisterViewModel.ts` 관례).
 * 🚫 `businessType` 은 검증하지 않는다 — `Select` 가 항상 유효한 기본값을 갖는다(빈 선택 상태가 없다).
 */
export function validateOperationVerificationForm(form: OperationVerificationFormState): VenueProfileFieldError[] {
  const errors: VenueProfileFieldError[] = []

  // 🚨 대표자 성명은 더 이상 클라이언트 입력값이 아니다(`#867` 자율 결정 5) — 서버가 prefill·제출
  // 양쪽에서 회원 실명으로 확정하므로 입력칸은 읽기전용이다. 실명이 비어 있으면(미인증 계정) 여기서
  // REQUIRED 오류를 만드는 대신 페이지가 제출 전에 인라인 경고("본인인증 후 신청할 수 있습니다")로
  // 안내한다 — 사용자가 고칠 수 없는 필드를 오류로 하이라이트해도 다음 행동을 알려주지 못한다.

  const businessNumberDigits = stripNonDigits(form.businessNumber)
  if (businessNumberDigits.length === 0) {
    errors.push({
      field: 'businessNumber',
      code: 'REQUIRED',
      message: `${FIELD_LABELS.businessNumber}을(를) 입력해 주세요.`,
    })
  } else if (!BUSINESS_NUMBER_PATTERN.test(businessNumberDigits)) {
    errors.push({
      field: 'businessNumber',
      code: 'INVALID_FORMAT',
      message: '사업자등록번호는 숫자 10자리로 입력해 주세요. (예: 000-00-00000)',
    })
  }

  const contactDigits = stripNonDigits(form.contact)
  if (contactDigits.length === 0) {
    errors.push({ field: 'contact', code: 'REQUIRED', message: `${FIELD_LABELS.contact}을(를) 입력해 주세요.` })
  } else if (!CONTACT_PATTERN.test(contactDigits)) {
    errors.push({ field: 'contact', code: 'INVALID_FORMAT', message: '연락처는 숫자 9~11자리로 입력해 주세요.' })
  }

  const address = form.operationAddress.trim()
  if (address.length > OPERATION_VERIFICATION_LIMITS.operationAddress) {
    errors.push({
      field: 'operationAddress',
      code: 'TOO_LONG',
      message: `${FIELD_LABELS.operationAddress}은(는) 최대 ${OPERATION_VERIFICATION_LIMITS.operationAddress}자까지 입력할 수 있습니다.`,
    })
  }

  if (form.operationStartedOn.trim().length > 0 && form.operationStartedOn > todayKstDate()) {
    errors.push({
      field: 'operationStartedOn',
      code: 'FUTURE_NOT_ALLOWED',
      message: '운영 시작일은(는) 오늘 이후로 지정할 수 없습니다.',
    })
  }

  const note = form.additionalNote.trim()
  if (note.length > OPERATION_VERIFICATION_LIMITS.additionalNote) {
    errors.push({
      field: 'additionalNote',
      code: 'TOO_LONG',
      message: `${FIELD_LABELS.additionalNote}은(는) 최대 ${OPERATION_VERIFICATION_LIMITS.additionalNote}자까지 입력할 수 있습니다.`,
    })
  }

  return errors
}

/** 서류 파일 사전 검증(api-spec §3) — 확장자·용량. 통과하면 `null`. */
export function validateOperationVerificationDocument(file: File): string | null {
  const extension = file.name.split('.').pop()?.toLowerCase() ?? ''
  if (!OPERATION_VERIFICATION_DOCUMENT_LIMITS.allowedExtensions.includes(extension)) {
    return `${OPERATION_VERIFICATION_DOCUMENT_LIMITS.allowedExtensionsLabel} 파일만 첨부할 수 있습니다.`
  }
  if (file.size > OPERATION_VERIFICATION_DOCUMENT_LIMITS.maxSizeBytes) {
    return '파일 용량은 10MB를 넘을 수 없습니다.'
  }
  return null
}

/** 빈 문자열/공백뿐인 문자열은 미입력(`null`)으로 정규화한다(`venueProfileViewModel.ts` 와 동일 규칙). */
function toNullableText(value: string): string | null {
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

/**
 * 폼 → `POST` 요청 바디. 검증을 통과한 뒤에만 호출한다.
 * 🚨 서류는 `document.status === 'done'` 일 때만 실어 보낸다 — `idle`(prefill 표시뿐, 재첨부 전)·
 * `error` 상태는 미첨부 제출로 취급한다(api-spec §2 "서류 미첨부 제출은 허용된다").
 */
export function toOperationVerificationSubmitInput(
  form: OperationVerificationFormState
): OperationVerificationSubmitInput {
  const attached = form.document.status === 'done'

  return {
    businessType: form.businessType,
    representativeName: form.representativeName.trim(),
    businessNumber: stripNonDigits(form.businessNumber),
    contact: stripNonDigits(form.contact),
    operationAddress: toNullableText(form.operationAddress),
    operationStartedOn: toNullableText(form.operationStartedOn),
    documentKey: attached ? form.document.documentKey : null,
    documentFileName: attached ? form.document.fileName : null,
    additionalNote: toNullableText(form.additionalNote),
  }
}
